import { useEffect, type RefObject } from 'react';

/**
 * Keep-alive host for the noVNC iframe.
 *
 * A VNC connection cannot survive its `<iframe>` being unmounted or re-parented
 * — the browser tears the document down and noVNC has to hand-shake again. React
 * re-creates the session panel far too readily for that: a session poll landing,
 * a status flap flipping the sandbox-online gate, or switching tabs is enough to
 * blank the desktop mid-stream.
 *
 * So the iframe is not React-managed. It lives in a `position: fixed` container
 * appended to `<body>` and is only ever *positioned* over the slot the component
 * renders. React mounting and unmounting then just shows and hides it, and the
 * connection outlives every re-render of the tree above it.
 *
 * One surface exists at a time: opening a different session's desktop discards
 * the previous one.
 */
interface Surface {
  key: string;
  container: HTMLDivElement;
}

let surface: Surface | null = null;

function acquireSurface(key: string, src: string): HTMLDivElement {
  if (surface && surface.key !== key) {
    surface.container.remove();
    surface = null;
  }

  if (!surface) {
    const container = document.createElement('div');
    container.style.position = 'fixed';
    // Kept a literal rather than a var() so a missing custom property can never
    // degrade this to `auto` and drop the desktop behind the app. Paired with
    // `--z-index-panel-resizer` in globals.css, which must stay above it so the
    // panel's drag handle is grabbable over the iframe.
    container.style.zIndex = '10';
    container.style.background = '#000';
    container.style.visibility = 'hidden';

    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.title = 'Sandbox desktop';
    iframe.allow = 'clipboard-read; clipboard-write';
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = '0';

    container.appendChild(iframe);
    document.body.appendChild(container);
    surface = { key, container };
  }

  return surface.container;
}

/**
 * Track `slot`'s box and keep the keep-alive surface glued to it, hidden
 * whenever the slot has no box (an inactive tab collapses to 0×0) or the
 * component is unmounted.
 */
export function useDesktopSurface(key: string, src: string | null, slot: RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const node = slot.current;
    if (!src || !node) return;

    const container = acquireSurface(key, src);

    const sync = () => {
      const rect = node.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        container.style.visibility = 'hidden';
        return;
      }
      container.style.left = `${rect.left}px`;
      container.style.top = `${rect.top}px`;
      container.style.width = `${rect.width}px`;
      container.style.height = `${rect.height}px`;
      container.style.visibility = 'visible';
    };

    sync();

    const observer = new ResizeObserver(sync);
    observer.observe(node);
    observer.observe(document.documentElement);
    // Capture phase: the panel scrolls inside the app shell, not the document.
    window.addEventListener('scroll', sync, true);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', sync, true);
      container.style.visibility = 'hidden';
    };
  }, [key, src, slot]);
}
